import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

import { default as esql_parser, IndexPatternContext } from '../antlr/esql_parser';
import { createCommand, createFunction, createOption, createLiteral, textExistsAndIsValid, createSource, createAstBaseItem } from './factories';
import { getPosition } from './helpers';
import { collectAllSourceIdentifiers, collectAllFields, collectAllAggFields, visitByOption, collectAllColumnIdentifiers, visitRenameClauses, visitOrderExpressions, getPolicyName, getMatchField, getEnrichClauses } from './walkers';
import { createJoinCommand } from './factories/join';
import { createDissectCommand } from './factories/dissect';
import { createGrokCommand } from './factories/grok';
import { createStatsCommand } from './factories/stats';
import { createChangePointCommand } from './factories/change_point';
import { createWhereCommand } from './factories/where';
import { createRowCommand } from './factories/row';
export class ESQLAstBuilderListener {
  constructor(src) {
    _defineProperty(this, "ast", []);
    this.src = src;
  }
  getAst() {
    return {
      ast: this.ast
    };
  }

  /**
   * Exit a parse tree produced by the `showInfo`
   * labeled alternative in `esql_parser.showCommand`.
   * @param ctx the parse tree
   */
  exitShowInfo(ctx) {
    const commandAst = createCommand('show', ctx);
    this.ast.push(commandAst);
    commandAst.text = ctx.getText();
    if (textExistsAndIsValid(ctx.INFO().getText())) {
      // TODO: these probably should not be functions, instead use "column", like: INFO <identifier>?
      commandAst === null || commandAst === void 0 ? void 0 : commandAst.args.push(createFunction('info', ctx, getPosition(ctx.INFO().symbol)));
    }
  }

  /**
   * Enter a parse tree produced by `esql_parser.singleStatement`.
   * @param ctx the parse tree
   */
  enterSingleStatement(ctx) {
    this.ast = [];
  }

  /**
   * Exit a parse tree produced by `esql_parser.whereCommand`.
   * @param ctx the parse tree
   */
  exitWhereCommand(ctx) {
    const command = createWhereCommand(ctx);
    this.ast.push(command);
  }

  /**
   * Exit a parse tree produced by `esql_parser.rowCommand`.
   * @param ctx the parse tree
   */
  exitRowCommand(ctx) {
    const command = createRowCommand(ctx);
    this.ast.push(command);
  }

  /**
   * Exit a parse tree produced by `esql_parser.fromCommand`.
   * @param ctx the parse tree
   */
  exitFromCommand(ctx) {
    const commandAst = createCommand('from', ctx);
    this.ast.push(commandAst);
    commandAst.args.push(...collectAllSourceIdentifiers(ctx));
    const metadataContext = ctx.indexPatternAndMetadataFields().metadata();
    if (metadataContext && metadataContext.METADATA()) {
      const option = createOption(metadataContext.METADATA().getText().toLowerCase(), metadataContext);
      commandAst.args.push(option);
      option.args.push(...collectAllColumnIdentifiers(metadataContext));
    }
  }

  /**
   * Exit a parse tree produced by `esql_parser.metricsCommand`.
   * @param ctx the parse tree
   */
  exitMetricsCommand(ctx) {
    const node = {
      ...createAstBaseItem('metrics', ctx),
      type: 'command',
      args: [],
      sources: ctx.indexPatternAndMetadataFields().getTypedRuleContexts(IndexPatternContext).map(sourceCtx => createSource(sourceCtx))
    };
    this.ast.push(node);
    node.args.push(...node.sources);
  }

  /**
   * Exit a parse tree produced by `esql_parser.evalCommand`.
   * @param ctx the parse tree
   */
  exitEvalCommand(ctx) {
    const commandAst = createCommand('eval', ctx);
    this.ast.push(commandAst);
    commandAst.args.push(...collectAllFields(ctx.fields()));
  }

  /**
   * Exit a parse tree produced by `esql_parser.statsCommand`.
   * @param ctx the parse tree
   */
  exitStatsCommand(ctx) {
    const command = createStatsCommand(ctx, this.src);
    this.ast.push(command);
  }

  /**
   * Exit a parse tree produced by `esql_parser.inlinestatsCommand`.
   * @param ctx the parse tree
   */
  exitInlinestatsCommand(ctx) {
    const command = createCommand('inlinestats', ctx);
    this.ast.push(command);

    // STATS expression is optional
    if (ctx._stats) {
      command.args.push(...collectAllAggFields(ctx.aggFields()));
    }
    if (ctx._grouping) {
      command.args.push(...visitByOption(ctx, ctx.fields()));
    }
  }

  /**
   * Exit a parse tree produced by `esql_parser.limitCommand`.
   * @param ctx the parse tree
   */
  exitLimitCommand(ctx) {
    const command = createCommand('limit', ctx);
    this.ast.push(command);
    if (ctx.getToken(esql_parser.INTEGER_LITERAL, 0)) {
      const literal = createLiteral('integer', ctx.INTEGER_LITERAL());
      if (literal) {
        command.args.push(literal);
      }
    }
  }

  /**
   * Exit a parse tree produced by `esql_parser.sortCommand`.
   * @param ctx the parse tree
   */
  exitSortCommand(ctx) {
    const command = createCommand('sort', ctx);
    this.ast.push(command);
    command.args.push(...visitOrderExpressions(ctx.orderExpression_list()));
  }

  /**
   * Exit a parse tree produced by `esql_parser.keepCommand`.
   * @param ctx the parse tree
   */
  exitKeepCommand(ctx) {
    const command = createCommand('keep', ctx);
    this.ast.push(command);
    command.args.push(...collectAllColumnIdentifiers(ctx));
  }

  /**
   * Exit a parse tree produced by `esql_parser.dropCommand`.
   * @param ctx the parse tree
   */
  exitDropCommand(ctx) {
    const command = createCommand('drop', ctx);
    this.ast.push(command);
    command.args.push(...collectAllColumnIdentifiers(ctx));
  }

  /**
   * Exit a parse tree produced by `esql_parser.renameCommand`.
   * @param ctx the parse tree
   */
  exitRenameCommand(ctx) {
    const command = createCommand('rename', ctx);
    this.ast.push(command);
    command.args.push(...visitRenameClauses(ctx.renameClause_list()));
  }

  /**
   * Exit a parse tree produced by `esql_parser.dissectCommand`.
   * @param ctx the parse tree
   */
  exitDissectCommand(ctx) {
    const command = createDissectCommand(ctx);
    this.ast.push(command);
  }

  /**
   * Exit a parse tree produced by `esql_parser.grokCommand`.
   * @param ctx the parse tree
   */
  exitGrokCommand(ctx) {
    const command = createGrokCommand(ctx);
    this.ast.push(command);
  }

  /**
   * Exit a parse tree produced by `esql_parser.mvExpandCommand`.
   * @param ctx the parse tree
   */
  exitMvExpandCommand(ctx) {
    const command = createCommand('mv_expand', ctx);
    this.ast.push(command);
    command.args.push(...collectAllColumnIdentifiers(ctx));
  }

  /**
   * Enter a parse tree produced by `esql_parser.showCommand`.
   * @param ctx the parse tree
   */
  enterShowCommand(ctx) {
    const command = createCommand('show', ctx);
    this.ast.push(command);
  }

  /**
   * Exit a parse tree produced by `esql_parser.enrichCommand`.
   * @param ctx the parse tree
   */
  exitEnrichCommand(ctx) {
    const command = createCommand('enrich', ctx);
    this.ast.push(command);
    command.args.push(...getPolicyName(ctx), ...getMatchField(ctx), ...getEnrichClauses(ctx));
  }

  /**
   * Exit a parse tree produced by `esql_parser.joinCommand`.
   *
   * Parse the JOIN command:
   *
   * ```
   * <type> JOIN identifier [ AS identifier ] ON expression [, expression [, ... ]]
   * ```
   *
   * @param ctx the parse tree
   */
  exitJoinCommand(ctx) {
    const command = createJoinCommand(ctx);
    this.ast.push(command);
  }

  /**
   * Exit a parse tree produced by `esql_parser.changePointCommand`.
   *
   * Parse the CHANGE_POINT command:
   *
   * CHANGE_POINT <value> [ ON <key> ] [ AS <target-type>, <target-pvalue> ]
   *
   * @param ctx the parse tree
   */
  exitChangePointCommand(ctx) {
    const command = createChangePointCommand(ctx);
    this.ast.push(command);
  }
  enterEveryRule(ctx) {
    // method not implemented, added to satisfy interface expectation
  }
  visitErrorNode(node) {
    // method not implemented, added to satisfy interface expectation
  }
  visitTerminal(node) {
    // method not implemented, added to satisfy interface expectation
  }
  exitEveryRule(ctx) {
    // method not implemented, added to satisfy interface expectation
  }
}